import { Sources, Caption, MP4VideoTrack, VideoContentData } from '../Player/PlayerModels';
import { announceAutoplay, handleSimplePlayerSubs, timeToSeconds, updatePlayback } from '../Player/PlayerServices';
import { useCallback, useEffect, useRef, useState } from 'react';
import PlayerCustomComponents from './components/PlayerCustomComponents';
import PlayerVastComponents from './components/PlayerVastComponents';
import UnmuteNotice from './components/UnmuteNotice';
import * as Sentry from '@sentry/nextjs';
import ErrorModal from './components/ErrorModal';
import { useUser } from '../../CacheProvider';
import { __t } from '../../i18n';
import UsePlayerProps from '../../Store/usePlayerProps';
import lscache from 'lscache';
import SimpleVast from './Plugins/Vast';
import { VideoErrorReportRequest } from '../../apiService';
import createSimplePlayerDiagnostics from './diagnostics';
import { getDeviceInfo } from '../../utils';

export default function VideoPlayer(props: {
  toggleLoader?: () => void;
  isShort?: boolean;
  onShowInfo?: () => void;
  onEnded?: () => void;
}) {
  const setPlayerGlobalState = UsePlayerProps((state) => state.setPlayerState);
  const playerGlobalState = UsePlayerProps((state) => state.playerState);
  const { contentConfigs, contentData, sources } = playerGlobalState;
  const { videoFileType } = playerGlobalState.contentData as VideoContentData;
  const { user } = useUser();
  const { toggleLoader, isShort = false, onShowInfo, onEnded } = props;
  const { videos, captions } = sources as Sources;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const vastRef = useRef<InstanceType<typeof SimpleVast> | null>(null);
  const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const IS_MAC = /Macintosh|Mac OS/.test(navigator.userAgent);
  const intervalID = useRef<any>(null);
  const [showError, setShowError] = useState<boolean>(false);
  const [errorReportBody, setErrorReportBody] = useState<VideoErrorReportRequest | null>(null);
  // With ads, the content <source> is rendered only once the VAST plugin calls loadContent, so a preroll's
  // media isn't downloading alongside the content.
  const [canLoadContent, setCanLoadContent] = useState<boolean>(!contentConfigs?.vast_url);
  const [playingAd, setPlayingAd] = useState<boolean>(false);
  const playingAdRef = useRef<boolean>(false);
  const initialLoad = useRef<any>(false);
  const loaderHidden = useRef<boolean>(false);
  const savedPlayerState = useRef<{ playbackRate: number; captionTrackIndex: number } | null>(null);
  const diagnosticsRef = useRef<ReturnType<typeof createSimplePlayerDiagnostics> | null>(null);
  // Stable reference: the previous inline arrows meant removeEventListener never matched, and
  // the shorts page remounts this player per episode.
  const handleKeyDown = useCallback((e: KeyboardEvent) => handleKeyDownAction(e, videoRef.current), []);

  if (!diagnosticsRef.current) {
    diagnosticsRef.current = createSimplePlayerDiagnostics({
      contentId: contentData?.id || '',
      fileId: contentData?.videoFileId || '',
      sources: videos,
    });
  }

  useEffect(() => {
    const detachDiagnostics = videoRef.current ? diagnosticsRef.current?.attach(videoRef.current) : undefined;

    const offEntry: Caption = {
      language: 'off',
      label: __t('player.off'),
      mode: 'disabled',
      src: '',
      srclang: '',
      kind: 'subtitles',
      default: false,
    };
    const applyCaptionFiles = (files: Caption[]) => {
      const currentState = UsePlayerProps.getState().playerState;
      // Patch only captionFiles onto the freshest sources so we don't clobber fields set
      // by other async writers (e.g. the page's vttData) and vice-versa. Fall back to the
      // closure values if sources was momentarily cleared (e.g. during unmount).
      const baseSources = currentState.sources ?? { videos, captions };
      setPlayerGlobalState({
        ...currentState,
        sources: {
          ...baseSources,
          captionFiles: files,
        },
      });
    };
    handleSimplePlayerSubs(captions)
      .then((res) => applyCaptionFiles([offEntry, ...res]))
      .catch(() => applyCaptionFiles([offEntry, ...captions]));
    (document && videoRef.current) && document.addEventListener('keydown', handleKeyDown);
    return () => {
      clearPlaybackInterval();
      detachDiagnostics?.();
      document && document.removeEventListener('keydown', handleKeyDown);
      if (videoRef.current) videoRef.current = null;
      setPlayerGlobalState({
        contentConfigs: null,
        contentData: null,
        sources: null,
        vttData: null,
        captionState: {
          prevCaptionIndex: -1,
          selectedCaptionLang: {
            lang: 'off',
            label: 'خاموش',
          },
        },
        showingNextEpisode: lscache.get('player_config') ? lscache.get('player_config').autoNextEpisode : true,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A short reports its playback once, when its episode starts playing. The shorts page mounts
  // a player per episode, so a one-shot listener needs no bookkeeping of its own; ads run on
  // their own element, so this only ever sees the content start.
  useEffect(() => {
    const video = videoRef.current;
    if (!isShort || !video) return;
    const report = () => updatePlayback(
      contentData?.id, contentData?.videoFileId, video.currentTime, video.currentSrc, true,
    );
    video.addEventListener('playing', report, { once: true });
    return () => video.removeEventListener('playing', report);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The mp4 twin of `autoplay: 'any'` in the video.js player's PlayerConfigs: ask the browser
  // for autoplay with sound and, only when it refuses, retry muted so playback always starts.
  // The outcome is announced on the element under the name video.js gives it, so UnmuteNotice
  // hooks the same signal in both players.
  //
  // Not when there is an ad break to come: the Vast plugin holds the content back and plays the
  // ad first, so the ad is what the browser is asked to start and the plugin owns the question
  // there (_retryPlaybackMuted). Asking from both sides muted the content on a refusal the ad
  // would have answered for itself, and the plugin then had nothing left to fall back to.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || contentConfigs?.vast_url) return;

    video.play().then(() => announceAutoplay(video, true)).catch((err) => {
      // Only a refusal means muted playback is the one on offer; anything else is an
      // interruption, and muting over it would take the sound away behind the viewer's back.
      if (err?.name !== 'NotAllowedError' || !videoRef.current) return;
      videoRef.current.muted = true;
      videoRef.current.play()
        .then(() => announceAutoplay(videoRef.current, true))
        .catch(() => announceAutoplay(videoRef.current, false));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep ref in sync so keyboard handler can read it without stale closure
  useEffect(() => {
    playingAdRef.current = playingAd;
  }, [playingAd]);

  // VAST / VMAP ad plugin
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !contentConfigs?.vast_url) return;

    const onVastPlay = () => {
      // On iOS the content <video> never loads (and thus never fires a readiness
      // event) until playback resumes after the ad, so the loader must be cleared
      // off the ad starting instead.
      hideLoader();
      const v = videoRef.current;
      if (v) {
        savedPlayerState.current = {
          playbackRate: v.playbackRate,
          captionTrackIndex: Array.from(v.textTracks).findIndex((t) => t.mode === 'showing'),
        };
      }
      setPlayingAd(true);
    };

    const onVastComplete = () => {
      setPlayingAd(false);
      const saved = savedPlayerState.current;
      const v = videoRef.current;
      if (!saved || !v) return;
      v.addEventListener('playing', () => {
        v.playbackRate = saved.playbackRate;
        if (saved.captionTrackIndex >= 0) {
          Array.from(v.textTracks).forEach((t, i) => {
            t.mode = i === saved.captionTrackIndex ? 'showing' : 'disabled';
          });
        }
        savedPlayerState.current = null;
      }, { once: true });
    };

    const onVastError = () => {
      hideLoader();
      setPlayingAd(false);
    };

    video.addEventListener('vast.play', onVastPlay);
    video.addEventListener('vast.complete', onVastComplete);
    video.addEventListener('vast.error', onVastError);

    vastRef.current = new SimpleVast(video, {
      vmapUrl: contentConfigs.vast_url,
      addCtaClickZone: false,
      timeout: 15000,
      verificationTimeout: 5000,
      // The content isn't loaded during a preroll, so fall back to the quality it will load in.
      getContentQuality: () => getCurrentContentQuality(video, videos as MP4VideoTrack[]) ??
        (playableSource[0] as MP4VideoTrack)?.quality,
      loadContent: () => setCanLoadContent(true),
      debug: false,
    });

    return () => {
      video.removeEventListener('vast.play', onVastPlay);
      video.removeEventListener('vast.complete', onVastComplete);
      video.removeEventListener('vast.error', onVastError);
      vastRef.current?.dispose();
      vastRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAbsoluteSrc = (src = '') => {
    try {
      return new URL(src, window.location.href).href;
    } catch {
      return src;
    }
  };

  const getCurrentContentQuality = (video: HTMLVideoElement, sources: MP4VideoTrack[]) => {
    const currentSrc = getAbsoluteSrc(video.currentSrc || video.src);
    const currentSource = sources.find((source) => getAbsoluteSrc(source.src) === currentSrc);

    return currentSource?.quality;
  };

  const handleKeyDownAction = (e, player) => {
    if (playingAdRef.current) return;
    if (player) {
      const playerVolume = player.volume;
      const playerCurrentTime = player.currentTime;
      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (player.paused) {
            player.play();
          } else {
            player.pause();
          }
          break;
        case 'Enter':
          if (player.paused) {
            player.play();
          } else {
            player.pause();
          }
          break;
        case 'ArrowRight':
          player.currentTime = playerCurrentTime + 15;
          break;
        case 'ArrowLeft':
          player.currentTime = playerCurrentTime - 15;
          break;
        case 'ArrowUp':
          // Turning the volume up on a muted player (a muted autoplay, for instance) has to
          // unmute it, otherwise the key does nothing audible.
          player.muted = false;
          if (playerVolume !== 1) {
            player.volume = ((playerVolume * 10) + 1) / 10;
          }
          break;
        case 'ArrowDown':
          if (playerVolume !== 0) {
            player.volume = ((playerVolume * 10) - 1) / 10;
          }
          break;
        case 'KeyM':
          // Toggle the mute itself rather than the level: the player can sit muted with the
          // volume still at 1, and a level-only toggle leaves that case silent either way.
          player.muted = !player.muted;
          if (!player.volume) {
            player.volume = 1;
          }
          break;
        default:
          return;
      }
    }
  };

  // Hide the full-page loader exactly once, on the first readiness signal a
  // given device delivers (event order/availability varies across browsers).
  const hideLoader = () => {
    if (loaderHidden.current) return;
    loaderHidden.current = true;
    toggleLoader?.();
  };

  const setResume = () => {
    hideLoader();
    if (videoRef.current && !initialLoad.current) {
      videoRef.current.currentTime = timeToSeconds(contentConfigs?.recommended_start);
      diagnosticsRef.current?.addEvent('resume_seek', videoRef.current, {
        recommended_start: contentConfigs?.recommended_start,
      });
      initialLoad.current = true;
    }
  };

  const handlePlayback = () => {
    // Shorts report once from the effect below rather than on this interval.
    if (!intervalID.current && !playingAd && videoFileType !== 'trailer' && !isShort) {
      const intervalTimer = contentConfigs?.user_configuration?.playback_report_interval
        ? contentConfigs.user_configuration?.playback_report_interval * 1000
        : 30000;

      intervalID.current = setInterval(() => {
        if (videoRef.current && videoRef.current.currentTime > 0)
          updatePlayback(contentData?.id, contentData?.videoFileId, videoRef.current?.currentTime, videoRef.current.currentSrc);
      }, intervalTimer);
    }
  };

  const clearPlaybackInterval = () => {
    clearInterval(intervalID.current);
    intervalID.current = null;
  };

  const handlePause = () => {
    clearPlaybackInterval();
  };

  const handleEnded = () => {
    clearPlaybackInterval();
    // The ad plays through this same element, so only the content's end should advance.
    if (!playingAdRef.current) onEnded?.();
  };

  const extractEdgeName = (url: string) => {
    const match = url.match(/\bgp\d+\b/);
    return match ? match[0] : 'arvan';
  };

  const showFatalError = (error) => {
    setShowError(true);
    clearPlaybackInterval();
    // The error modal replaces the player subtree, so there is nowhere left to render an ad.
    // The plugin does not observe that, and an ad video attached to the detached wrapper still
    // plays its audio — so tear it down here rather than waiting for this component to unmount.
    vastRef.current?.dispose();
    vastRef.current = null;
    const deviceInfo = getDeviceInfo();
    const videoUrl = videoRef.current?.currentSrc || videos[0]?.src || '';
    const rawData = diagnosticsRef.current?.toRawData(videoRef.current, error) || '';

    setErrorReportBody({
      brand: deviceInfo.brand,
      model: deviceInfo.model,
      app_version: deviceInfo.app_version,
      os_version: deviceInfo.os_version,
      screen_width: deviceInfo.screen_width,
      screen_height: deviceInfo.screen_height,
      related_content_id: contentData?.id || '',
      related_file_id: contentData?.videoFileId || '',
      video_url: videoUrl,
      video_type: 'mp4',
      raw_data: rawData,
    });
    error.source = JSON.stringify(videos);
    Sentry.withScope((scope) => {
      scope.setUser({ id: user?.id, username: String(user?.msisdn) });
      scope.setTag('Tech', 'MP4');
      scope.setTag('ErrorCode', error.code);
      scope.setTag('Player', 'simple');
      scope.setTag('Edge', extractEdgeName(videoUrl));
      Sentry.captureException(error);
    });
  };

  const handleError = (err) => {
    hideLoader();
    // A MediaError keeps its code and message in prototype getters, so it has no own entries to
    // count. A <source> failure also reaches this handler, with no error of its own: that one is
    // handleSourceError's.
    const mediaError = err.target.error;
    if (mediaError) {
      showFatalError({ code: mediaError.code, message: mediaError.message });
    }
  };

  // A 404 on the video file fires the error event on the <source> element itself
  // (video.error stays null), so it never reaches handleError — show the modal here.
  const handleSourceError = (err) => {
    hideLoader();
    showFatalError({
      code: MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED,
      message: `source failed to load: ${err.target?.src || ''}`,
    });
  };

  if (showError) {
    return <ErrorModal reportBody={errorReportBody} isShort={isShort} />;
  }

  // Default the initial mp4 source to 720p (fallback to the lowest available quality).
  // Quality changes beyond this are applied imperatively by the quality panel via player.src,
  // so only the default source is rendered here.
  const defaultQualityIdx = (videos as MP4VideoTrack[]).findIndex((src) => src.quality === 720);
  const playableSource =
    defaultQualityIdx !== -1 ? [videos[defaultQualityIdx]] : videos.length ? [videos[videos.length - 1]] : videos;

  return (
    <div id="custom-player-wrapper" className={`${IS_IOS && 'native-custom-simple-player'} ${!IS_IOS && IS_MAC && 'hide-native-caption'} ${isShort ? 'short-player' : ''}`}>
      <video
        id="simple-player"
        ref={videoRef}
        autoPlay
        playsInline={IS_IOS}
        preload="metadata"
        controls={false}
        poster={`${contentData?.cover}?w=1080`}
        disablePictureInPicture
        disableRemotePlayback
        className="custom-player"
        onLoadedData={setResume}
        onLoadedMetadata={hideLoader}
        onCanPlay={hideLoader}
        onPlaying={hideLoader}
        onTimeUpdate={handlePlayback}
        onPause={handlePause}
        onEnded={handleEnded}
        onError={handleError}
      >
        {!canLoadContent || playableSource.length === 0 ? undefined : (
          <>
            {playableSource?.map(({ id, src, type }) => (
              <source key={id} src={src} type={type} onError={handleSourceError} />
            ))}
          </>
        )}
        {IS_IOS ? (
          captions?.length > 0 && captions.map((track) => (
            <track key={track.language} label={track.label} src={track.src} kind={track.kind} srcLang={track.srclang} />
          ))
        ) : (
          !!(sources?.captionFiles?.length) && sources.captionFiles.filter((t) => t.language !== 'off').map((track) => (
            <track key={track.language} src={track.src} kind={track.kind} srcLang={track.srclang} />
          ))
        )}
      </video>
      {!IS_IOS && <div id="custom-track"></div>}
      {videoRef.current && (
        <>
          {playingAd && <PlayerVastComponents player={videoRef.current} vastRef={vastRef} isShort={isShort} />}
          {!playingAd && playableSource.length && videos.length && (
            <PlayerCustomComponents player={videoRef.current} isShort={isShort} onShowInfo={onShowInfo} />
          )}
          {/* Outside both, and last, so the notice is shown over an ad as well as over the
              content, and its click zone stays on top of the one that toggles playback. */}
          <UnmuteNotice video={videoRef.current} />
        </>
      )}
    </div>
  );
}
